// "Moments few will ever know" — experiences shown on the home page carousel, each with its own article page.
// `featured: true` renders as a wide card.

export const experiences = [
  {
    slug: 'off-market-villa-mallorca',
    eyebrow: 'Residences — Mallorca',
    category: 'Residences — Mallorca',
    title: 'A clifftop villa above the Tramuntana coast, offered to members before it is ever listed',
    dek: 'Eight suites, a private stair to the sea and an owner who would rather not advertise. To rent for the season or to own outright.',
    byline: 'Arranged by Superluxury',
    date: 'For rent or purchase',
    readTime: '4 min read',
    cta: 'View the residence',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/63394d57-6885-405b-bee9-ccd352bc504e.jpg',
    alt: 'A stone clifftop villa with an infinity pool above the Mallorcan coast at sunset',
    caption: 'A private villa on the Tramuntana coast. Its address is shared only after a confidentiality agreement.',
    featured: true,
    body: [
      'The finest homes on the island rarely change hands in public. They pass quietly — between families, through lawyers, by introduction.',
      'This clifftop villa above the Tramuntana coast is one of them: eight suites, terraces cut into the rock, an infinity pool facing the sunset and a private stair down to the water.',
      'Members may take it for a week, a season or the year, fully staffed. For those who would rather own it, the sale is handled off-market and in confidence, with viewings arranged by private boat.',
      'Superluxury holds a small collection of residences like this across the Mediterranean, the Alps and the Gulf — each one seen in person before it is offered.',
    ],
  },
  {
    slug: 'private-car-collection',
    eyebrow: 'Collections — Monaco',
    category: 'Collections — Monaco',
    title: 'Drive, rent or acquire from a private collection of twelve grand tourers and hypercars',
    dek: 'A gallery garage opened by appointment, with the keys handed over for the coast road.',
    byline: 'Arranged by Superluxury',
    date: 'By appointment',
    readTime: '4 min read',
    cta: 'Arrange a viewing',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/9d5db971-0b0e-4755-8470-a1337c4a5a4b.jpg',
    alt: 'Classic and modern grand tourers under soft spotlights in a private gallery garage',
    caption: 'A private collection above Monaco. Photography inside the gallery is not permitted.',
    featured: false,
    body: [
      'Behind an unmarked door above Monaco stands a collection few have seen: twelve cars, from restored classics to hypercars delivered this year.',
      'Members may take any of them for a day on the coast road, keep one at their disposal for the season, or — when the owner is willing — acquire it outright.',
      'Superluxury also secures allocations of new limited-series cars through its network of dealers and collectors, often before they are announced.',
      'Every car is inspected, insured and delivered wherever you are staying.',
    ],
  },
  {
    slug: 'monaco-yacht-show',
    eyebrow: 'Yacht Show — Monaco',
    category: 'Yacht Show — Monaco',
    title: 'Board the superyachts of the Monaco Yacht Show privately, before the public opens the gates',
    dek: 'Private viewings, introductions to builders and owners, and a berth of your own for the week.',
    byline: 'Arranged by Superluxury',
    date: 'September',
    readTime: '5 min read',
    cta: 'Request access',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/5464a024-a7c5-49d3-96fe-d09d44fd1af3.jpg',
    alt: 'Superyachts moored in Port Hercule, Monaco, at golden hour',
    caption: 'Port Hercule during show week. Private viewings are arranged before and after public hours.',
    featured: false,
    body: [
      'Every September, the world’s most remarkable new yachts gather in Port Hercule. Most visitors see them from the quay.',
      'Members are taken aboard privately, in the hour before the gates open, with the builder or captain as their guide — and introduced to owners willing to charter or sell.',
      'A tender, a berth and a residence above the harbour can be held for the week. The same access is arranged at Cannes, Fort Lauderdale and Dubai.',
    ],
  },
  {
    slug: 'grand-prix-terrace',
    eyebrow: 'Formula 1 — Monaco',
    category: 'Formula 1 — Monaco',
    title: 'Watch the race from a private terrace above the harbour, then walk the paddock with the team',
    dek: 'Four days in May, a terrace that never appears on a hospitality list, and an evening in the garage when the crowds have gone.',
    byline: 'Arranged by Superluxury',
    date: 'May',
    readTime: '5 min read',
    cta: 'Access by introduction',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/afb288de-ea52-43d1-bb78-d167cfda1346.jpg',
    alt: 'A private terrace overlooking the Monaco street circuit on race day',
    caption: 'A private terrace above the harbour on race day. Terraces are held for one party each year.',
    featured: true,
    body: [
      'For four days in May, the world watches Monaco. For a few guests, the view comes from a private terrace above the harbour — owned by a family who lend it to no one else.',
      'The terrace overlooks the climb from Sainte-Dévote, with a chef, sommelier and staff for the full weekend, and a tender waiting below for a lap of the harbour after the race.',
      'On Sunday evening, once the grandstands have emptied, guests are taken into the paddock to meet the team and walk the garage.',
      'The same access is arranged at Abu Dhabi, Silverstone and Las Vegas — terraces, yacht berths and residences, often a full year ahead.',
    ],
  },
  {
    slug: 'chefs-table',
    eyebrow: 'Fine Dining — Paris',
    category: 'Fine Dining — Paris',
    title: 'The chef’s table at a three-star kitchen, on a night it is fully booked',
    dek: 'Two seats at the pass, a menu written for the evening and a cellar opened on request.',
    byline: 'Arranged by Superluxury',
    date: 'Year-round',
    readTime: '3 min read',
    cta: 'Reserve the table',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/0ad64bf4-2f2a-4bc5-ac99-55cad57c430a.jpg',
    alt: 'A chef plating a refined dish beside a marble counter set for two',
    caption: 'A chef’s table in Paris. Reservations are held for members, never released online.',
    featured: false,
    body: [
      'The most coveted tables in the world are booked months ahead. A few are never offered at all.',
      'Superluxury holds standing reservations at three-star kitchens and private dining rooms from Paris to Tokyo — the chef’s table, the cellar, the salon upstairs.',
      'Menus can be written for the evening, wines chosen in advance, and the room closed to others for a celebration.',
    ],
  },
  {
    slug: 'palace-suite',
    eyebrow: 'Hotels — Paris',
    category: 'Hotels — Paris',
    title: 'The palace suite facing the Eiffel Tower, held for the week that every other room is gone',
    dek: 'Upgrades, late arrivals and a butler who already knows how you take your coffee.',
    byline: 'Arranged by Superluxury',
    date: 'Year-round',
    readTime: '3 min read',
    cta: 'Hold the suite',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/d7e27f72-553c-4c2a-a49b-e129599cb5a8.jpg',
    alt: 'A palace hotel suite at night with open windows facing the lit Eiffel Tower',
    caption: 'A palace suite in Paris during fashion week. Preferences are shared ahead of every arrival.',
    featured: true,
    body: [
      'During fashion week, race weekends and the holidays, the finest suites in a city are gone a year in advance.',
      'Through long relationships with palace hotels in Paris, London, Dubai and the Riviera, Superluxury holds them for members — with every preference shared before you arrive.',
      'Late check-outs, private entrances and floors closed for security are arranged without being asked.',
    ],
  },
  {
    slug: 'superyacht-charter',
    eyebrow: 'Charter — Dubai',
    category: 'Charter — Dubai',
    title: 'A sixty-metre superyacht for the weekend, crewed and waiting at the marina',
    dek: 'Charter for a night, a week or the season — or meet the owner if you would rather keep her.',
    byline: 'Arranged by Superluxury',
    date: 'October — April',
    readTime: '3 min read',
    cta: 'Charter the yacht',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/5172a220-0e5d-4eca-abd3-21cae4d8eaf6.jpg',
    alt: 'A superyacht deck at dusk with the Dubai skyline in the distance',
    caption: 'Off the Dubai coast at dusk. Charters are arranged with owners in our private fleet.',
    featured: false,
    body: [
      'Superluxury works directly with owners of a private fleet of superyachts, many of which never appear on a charter list.',
      'A yacht can be ready within a day, crewed and provisioned, for an evening off the Dubai skyline or a week through the Gulf.',
      'For members considering ownership, introductions to owners and builders are made in confidence.',
    ],
  },
];
